import React, { useState, useEffect } from 'react';
import { Menu, X, Instagram, Phone } from 'lucide-react';
import { restaurantInfo } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-md py-3'
          : 'bg-white/90 backdrop-blur-sm py-4'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <h1 className="text-2xl md:text-3xl font-bold font-serif" style={{ color: '#501414' }}>
              La Grande Brune
            </h1>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('concept')}
              className="text-black hover:text-gray-600 transition-colors duration-200 font-medium"
            >
              Concept
            </button>
            <button
              onClick={() => scrollToSection('dejeuner')}
              className="text-black hover:text-gray-600 transition-colors duration-200 font-medium"
            >
              Déjeuner
            </button>
            <button
              onClick={() => scrollToSection('diner')}
              className="text-black hover:text-gray-600 transition-colors duration-200 font-medium"
            >
              Dîner
            </button>
            <button
              onClick={() => scrollToSection('bar')}
              className="text-black hover:text-gray-600 transition-colors duration-200 font-medium"
            >
              Bar
            </button>
            <button
              onClick={() => scrollToSection('carte')}
              className="text-black hover:text-gray-600 transition-colors duration-200 font-medium"
            >
              Carte
            </button>
            <button
              onClick={() => scrollToSection('privatisation')}
              className="text-black hover:text-gray-600 transition-colors duration-200 font-medium"
            >
              Privatisation
            </button>
            <button
              onClick={() => scrollToSection('galerie')}
              className="text-black hover:text-gray-600 transition-colors duration-200 font-medium"
            >
              Galerie
            </button>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href={`https://instagram.com/${restaurantInfo.instagram}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-black hover:text-gray-600 transition-colors duration-200"
              aria-label="Instagram"
            >
              <Instagram size={24} />
            </a>
            <a
              href={`tel:${restaurantInfo.phone}`}
              className="flex items-center space-x-2 text-black hover:text-gray-600 transition-colors duration-200"
            >
              <Phone size={18} />
              <span className="font-medium">{restaurantInfo.phone}</span>
            </a>
            <a
              href={restaurantInfo.reservationLink}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-6 py-3 rounded-md font-semibold hover:bg-gray-800 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Réserver
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-black hover:text-gray-600 transition-colors duration-200"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg py-4 px-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-4">
              <button
                onClick={() => scrollToSection('concept')}
                className="text-left text-black hover:text-gray-600 transition-colors duration-200 font-medium py-2"
              >
                Concept
              </button>
              <button
                onClick={() => scrollToSection('dejeuner')}
                className="text-left text-black hover:text-gray-600 transition-colors duration-200 font-medium py-2"
              >
                Déjeuner
              </button>
              <button
                onClick={() => scrollToSection('diner')}
                className="text-left text-black hover:text-gray-600 transition-colors duration-200 font-medium py-2"
              >
                Dîner
              </button>
              <button
                onClick={() => scrollToSection('bar')}
                className="text-left text-black hover:text-gray-600 transition-colors duration-200 font-medium py-2"
              >
                Bar
              </button>
              <button
                onClick={() => scrollToSection('carte')}
                className="text-left text-black hover:text-gray-600 transition-colors duration-200 font-medium py-2"
              >
                Carte
              </button>
              <button
                onClick={() => scrollToSection('privatisation')}
                className="text-left text-black hover:text-gray-600 transition-colors duration-200 font-medium py-2"
              >
                Privatisation
              </button>
              <button
                onClick={() => scrollToSection('galerie')}
                className="text-left text-black hover:text-gray-600 transition-colors duration-200 font-medium py-2"
              >
                Galerie
              </button>
              <div className="pt-4 border-t border-gray-200">
                <a
                  href={`https://instagram.com/${restaurantInfo.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-black hover:text-gray-600 transition-colors duration-200 py-2"
                >
                  <Instagram size={18} />
                  <span className="font-medium">@{restaurantInfo.instagram}</span>
                </a>
                <a
                  href={`tel:${restaurantInfo.phone}`}
                  className="flex items-center space-x-2 text-black hover:text-gray-600 transition-colors duration-200 py-2"
                >
                  <Phone size={18} />
                  <span className="font-medium">{restaurantInfo.phone}</span>
                </a>
                <a
                  href={restaurantInfo.reservationLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-black text-white px-6 py-3 rounded-md font-semibold text-center hover:bg-gray-800 transition-colors duration-200 mt-4"
                >
                  Réserver
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
